import { useEffect, type ReactNode } from 'react';
import { Toaster } from '../components/ui/sonner';
import { TooltipProvider } from '../components/ui/tooltip';
import { ConfirmProvider } from '../components/confirm';
import { useResolvedTheme } from './useThemeMode';

export interface ThemeProviderProps {
  children: ReactNode;
  /** Set to 'rtl' when an RTL language is active. */
  direction?: 'ltr' | 'rtl';
}

/**
 * Applies the resolved theme and mounts the app-wide overlays: the toast
 * outlet, the tooltip delay group, and the confirm dialog behind `useConfirm`.
 *
 * The theme is a `.dark` class on `<html>`, which is what every `dark:` utility
 * and the token block in `styles/global.css` key off. `data-theme` is mirrored
 * alongside it for anything outside Tailwind.
 */
export function ThemeProvider({ children, direction = 'ltr' }: ThemeProviderProps) {
  const resolved = useResolvedTheme();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.setAttribute('data-theme', resolved);
    root.setAttribute('dir', direction);
  }, [resolved, direction]);

  return (
    <TooltipProvider>
      <ConfirmProvider>
        {children}
        <Toaster position="bottom-right" dir={direction} />
      </ConfirmProvider>
    </TooltipProvider>
  );
}
